import type { Employee } from "../employees/employees.interface";

export type PrintCardPrintSides = "4/0" | "4/4" | "5/0" | "5/5";
export type PreviewLayoutMode = "row" | "column";
export type PrintCardFontWeight = 400 | 500 | 600 | 700;
export type PrintCardOrientation = "landscape" | "portrait";

export type PrintCardTextVariable =
  | "userName"
  | "jobTitle"
  | "phone"
  | "email"
  | "companyName"
  | "companyAddress"
  | "tagline"
  | "custom"
  | "squash";

export interface PrintCardTextSquashStyle {
  fontSizePt?: number;
  fontWeight?: PrintCardFontWeight;
  italic?: boolean;
  uppercase?: boolean;
  color?: string;
  lineHeight?: number;
}

export interface PrintCardTextSquashPart extends PrintCardTextSquashStyle {
  variable: string;
  text?: string;
}

export type PrintCardElementType =
  "text" | "logo" | "image" | "qr" | "nfc" | "shape";

export type PrintCardTextAlign = "left" | "center" | "right";
export type PrintCardVerticalAlign = "top" | "middle" | "bottom";
export type PrintCardTextFit = "none" | "shrink";

export interface PrintCardElementBase {
  id: string;
  type: PrintCardElementType;
  xCm: number;
  yCm: number;
  widthCm: number;
  heightCm: number;
  zIndex: number;
  name?: string;
  rotationDeg?: number;
  reversed?: boolean;
}

export interface PrintCardTextElement extends PrintCardElementBase {
  type: "text";
  variable?: string;
  text?: string;
  squashParts?: PrintCardTextSquashPart[];
  squashSeparator?: string;
  squashSeparatorStyle?: PrintCardTextSquashStyle;
  fontSizePt?: number;
  fontWeight?: PrintCardFontWeight;
  italic?: boolean;
  uppercase?: boolean;
  color?: string;
  align?: PrintCardTextAlign;
  valign?: PrintCardVerticalAlign;
  textFit?: PrintCardTextFit;
  maxLines?: number;
  minFontSizePt?: number;
  lineHeight?: number;
}

export const PRINT_CARD_DEFAULT_SQUASH_SEPARATOR = " · ";

export interface PrintCardLogoElement extends PrintCardElementBase {
  type: "logo";
  srcVariable?: "companyImageUrl";
  srcUrl?: string;
  align?: PrintCardTextAlign;
  valign?: PrintCardVerticalAlign;
}

export interface PrintCardImageElement extends PrintCardElementBase {
  type: "image";
  srcUrl?: string;
  align?: PrintCardTextAlign;
  valign?: PrintCardVerticalAlign;
}

export interface PrintCardQrElement extends PrintCardElementBase {
  type: "qr";
  srcVariable?: "QRdigitalCardUrl";
}

export interface PrintCardNfcElement extends PrintCardElementBase {
  type: "nfc";
  srcVariable?: "nfcIcon";
  color?: string;
}

export interface PrintCardShapeElement extends PrintCardElementBase {
  type: "shape";
  fill?: string;
  borderRadiusMm?: number;
}

export type PrintCardElement =
  | PrintCardTextElement
  | PrintCardLogoElement
  | PrintCardImageElement
  | PrintCardQrElement
  | PrintCardNfcElement
  | PrintCardShapeElement;

export interface PrintCardSideDesign {
  backgroundColor: string;
  backgroundImageUrl?: string | null;
  elements: PrintCardElement[];
}

export interface PrintCardDesignPage {
  widthCm: number;
  heightCm: number;
  bleedMm: number;
  safeZoneInsetMm: number;
}

export interface PrintCardElementPosition {
  xCm: number;
  yCm: number;
}

export interface PrintCardSizeSideLayout {
  backgroundColor: string;
  backgroundImageUrl?: string | null;
  positions: Record<string, PrintCardElementPosition>;
}

export interface PrintCardSizeLayout {
  page: PrintCardDesignPage;
  front: PrintCardSizeSideLayout;
  back: PrintCardSizeSideLayout;
}

export interface PrintCardDesignDocument {
  designVersion: 3 | 4;
  layoutKey?: string | null;
  layoutFrontVariantKey?: string | null;
  layoutBackVariantKey?: string | null;
  orientation?: PrintCardOrientation;
  page: PrintCardDesignPage;
  front: PrintCardSideDesign;
  back: PrintCardSideDesign;
  activePageSizePresetId?: string;
  sizeLayouts?: Record<string, PrintCardSizeLayout>;
}

export interface PrintCardCompiledSizeContent {
  frontContent: string;
  backContent: string;
  frontBackgroundColor?: string;
  backBackgroundColor?: string;
  frontBackgroundImageUrl?: string | null;
  backBackgroundImageUrl?: string | null;
}

export type DialogEmployee = Employee;

export interface PrintCardPageLayoutSettings {
  pageSizePresetId: string;
  pageWidthCm: number;
  pageHeightCm: number;
  pageBleedMm: number;
  cornerRadiusMm?: number;
}

export interface PrintCardTemplateSettings {
  designVersion?: number;
  designDocument?: PrintCardDesignDocument | null;
  contentsByPageSizePresetId?: Record<string, PrintCardCompiledSizeContent>;
  orientation?: PrintCardOrientation;
  fontName?: string;
  fontFamily?: string;
  fontCssUrl?: string | null;
  printerPaperId?: number;
  printSides?: PrintCardPrintSides;
  printerSpacerColorId?: number;
  layoutKey?: string | null;
  layoutFrontVariantKey?: string | null;
  layoutBackVariantKey?: string | null;
  frontBackgroundColor?: string;
  backBackgroundColor?: string;
  frontBackgroundImageUrl?: string | null;
  backBackgroundImageUrl?: string | null;
  frontTextColor?: string;
  backTextColor?: string;
  frontNfcColor?: string;
  backNfcColor?: string;
  fontSizeNamePt?: number;
  fontSizeTitlePt?: number;
  fontSizeBodyPt?: number;
  fontSizeCompanyPt?: number;
  fontSizeTaglinePt?: number;
  fontWeightName?: PrintCardFontWeight;
  fontWeightTitle?: PrintCardFontWeight;
  fontWeightBody?: PrintCardFontWeight;
  fontWeightCompany?: PrintCardFontWeight;
  fontWeightTagline?: PrintCardFontWeight;
  fontItalicName?: boolean;
  fontItalicTitle?: boolean;
  fontItalicBody?: boolean;
  fontItalicCompany?: boolean;
  fontItalicTagline?: boolean;
  fontUppercaseName?: boolean;
  fontUppercaseTitle?: boolean;
  fontUppercaseBody?: boolean;
  fontUppercaseCompany?: boolean;
  fontUppercaseTagline?: boolean;
  logoMaxWidthCm?: number;
  logoMaxHeightCm?: number;
  nfcSizeCm?: number;
  qrSizeCm?: number;
  taglineText?: string | null;
  showLogo?: boolean;
  showJobTitle?: boolean;
  showContact?: boolean;
  showAddress?: boolean;
  showCompanyName?: boolean;
  allowedPageSizePresetIds?: string[];
  allowedQuantities?: number[];
  profileLanguage?: string | null;
}

export interface PrintCardSettings
  extends PrintCardTemplateSettings, PrintCardPageLayoutSettings {}

export type PrintCardTemplateType = "PAPER" | "PAPER_NFC" | "PLASTIC_NFC";

export interface PrintCardTemplateApproval {
  approverEmail: string;
  requestedByEmail: string;
  requestedAt: string | null;
  notifiedAt: string | null;
  approvedAt: string | null;
  approvedByEmail: string | null;
}

export interface PrintCardTemplateItem {
  printCardTemplateId: string;
  domain: string | null;
  type: PrintCardTemplateType;
  name: string;
  frontContent: string;
  backContent: string;
  settings: PrintCardTemplateSettings;
  digitalCardProfileId?: string | null;
  isActive: boolean;
  isPreview: boolean;
  isPendingApproval?: boolean;
  approval?: PrintCardTemplateApproval | null;
  hasOrders?: boolean;
  createdAt: string;
  modifiedAt: string;
}

export interface PrintCardPreviewResult {
  frontDocumentHtml: string;
  backDocumentHtml: string;
  pageWidthCm: number;
  pageHeightCm: number;
  orientation: PrintCardOrientation;
}

export interface PrintCardOrderItemPreviewResult {
  frontDocumentHtml: string;
  backDocumentHtml: string;
  pageWidthCm: number;
  pageHeightCm: number;
}

export interface PrintCardTemplatePayload {
  type: PrintCardTemplateType;
  name: string;
  frontContent: string;
  backContent: string;
  settings?: Partial<PrintCardTemplateSettings>;
  digitalCardProfileId?: string | null;
}

export interface PrintCardTemplateListResponse {
  templates: PrintCardTemplateItem[];
}

export type PrintCardLayoutBrandSide = "left" | "right" | "stack-left";
export type PrintCardLayoutMarker = "none" | "nfc" | "qr";
export type PrintCardLayoutBackStyle = "blank" | "tagline" | "brand";

export interface PrintCardLayoutVariantMeta {
  brandSide?: PrintCardLayoutBrandSide;
  marker?: PrintCardLayoutMarker;
  backStyle?: PrintCardLayoutBackStyle;
}

export interface PrintCardLayoutVariant {
  id: string;
  layoutKey: string;
  key: string;
  side: "front" | "back";
  name: string;
  content: string;
  contentPortrait?: string;
  compatibleTypes: PrintCardTemplateType[];
  meta?: PrintCardLayoutVariantMeta;
  sortOrder: number;
  isActive: boolean;
}

export interface PrintCardLayoutCatalogItem {
  key: string;
  name: string;
  sortOrder: number;
  fronts: PrintCardLayoutVariant[];
  backs: PrintCardLayoutVariant[];
}

export interface PrintCardLayoutCatalogResponse {
  layouts: PrintCardLayoutCatalogItem[];
}

export interface PrintCardLayoutCatalogPreviewResult {
  thumbs: Record<string, string>;
}

export interface PrintCardPageSizePresetListResponse {
  presets: PrintCardPageSizePreset[];
}

export interface PrintCardPreviewDraftPayload {
  email: string;
  type: PrintCardTemplateType;
  frontContent: string;
  backContent?: string;
  digitalCardProfileId?: string | null;
  profileLanguage?: string | null;
  settings?: Partial<PrintCardSettings>;
}

export interface PrintCardDownloadFile {
  blob: Blob;
  filename: string;
}

export interface PrintCardPdfFormatDialogData {
  presets: PrintCardPageSizePreset[];
  selectedPresetId?: string;
}

export interface PrintCardPdfFormatDialogResult {
  pageSizePresetId: string;
}

export interface PrintCardBulkDownloadDialogData {
  presets: PrintCardPageSizePreset[];
  selectedPresetId?: string;
}

export interface PrintCardBulkDownloadDialogResult {
  pageSizePresetId: string;
  emails: string[];
}

export interface PrintCardPrinterSettingsDefaults {
  paperId: number;
  printSides: PrintCardPrintSides;
  spacerColorId?: number;
}

export interface PrintCardPrinterSettingsResponse {
  enabled: boolean;
  productLine: "business_card" | "multiloft" | "plastic" | null;
  papers: PrintCardPrinterOption[];
  printSides: PrintCardPrintSides[];
  spacerColors?: PrintCardPrinterOption[];
  defaults: PrintCardPrinterSettingsDefaults;
}

export type PrintCardMaterial = "paper" | "plastic";

export interface PrintCardPageSizePreset {
  id: string;
  labelKey: string;
  material: PrintCardMaterial;
  widthCm: number;
  heightCm: number;
  bleedMm: number;
  safeZoneInsetMm: number;
  cornerRadiusMm?: number;
}

export interface PrintCardFontPreset {
  id: string;
  labelKey?: string;
  label?: string;
  fontFamily: string;
  fontCssUrl?: string | null;
}

export const PRINT_CARD_DEFAULT_PAGE_SIZE_PRESET_ID = "paper-cee-russia";
export const PRINT_CARD_DOWNLOAD_BLEED_MM = 3;
export const PRINT_CARD_PAPER_SAFE_ZONE_INSET_MM = 3;
export const PRINT_CARD_PLASTIC_SAFE_ZONE_INSET_MM = 5;

export const PRINT_CARD_DEFAULT_SIDE_BACKGROUND = "#ffffff";
export const PRINT_CARD_DEFAULT_SIDE_TEXT = "#000000";
export const PRINT_CARD_DEFAULT_NFC_COLOR = "#000000";
export const PRINT_CARD_DEFAULT_FONT_SIZE_NAME_PT = 14;
export const PRINT_CARD_DEFAULT_FONT_SIZE_TITLE_PT = 10;
export const PRINT_CARD_DEFAULT_FONT_SIZE_BODY_PT = 7;
export const PRINT_CARD_DEFAULT_FONT_SIZE_COMPANY_PT = 7;
export const PRINT_CARD_DEFAULT_FONT_SIZE_TAGLINE_PT = 12;
export const PRINT_CARD_MIN_FONT_SIZE_PT = 3;
export const PRINT_CARD_DEFAULT_MIN_FONT_SIZE_PT = 5;
export const PRINT_CARD_DEFAULT_FONT_WEIGHT_NAME: PrintCardFontWeight = 700;
export const PRINT_CARD_DEFAULT_FONT_WEIGHT_TITLE: PrintCardFontWeight = 400;
export const PRINT_CARD_DEFAULT_FONT_WEIGHT_BODY: PrintCardFontWeight = 400;
export const PRINT_CARD_DEFAULT_FONT_WEIGHT_COMPANY: PrintCardFontWeight = 700;
export const PRINT_CARD_DEFAULT_FONT_WEIGHT_TAGLINE: PrintCardFontWeight = 700;
export const PRINT_CARD_FONT_WEIGHT_OPTIONS: PrintCardFontWeight[] = [
  400, 500, 600, 700,
];

export const PRINT_CARD_TEXT_HEIGHT_NAME_CM = 0.58;
export const PRINT_CARD_TEXT_HEIGHT_TITLE_CM = 0.46;
export const PRINT_CARD_TEXT_HEIGHT_BODY_CM = 0.4;
export const PRINT_CARD_TEXT_HEIGHT_ADDRESS_CM = 0.42;
export const PRINT_CARD_TEXT_HEIGHT_CONTACT_CM = 0.42;
export const PRINT_CARD_TEXT_HEIGHT_STACK_NAME_CM = 0.52;
export const PRINT_CARD_TEXT_HEIGHT_STACK_TITLE_CM = 0.38;
export const PRINT_CARD_TEXT_HEIGHT_SPLIT_TITLE_CM = 0.4;
export const PRINT_CARD_TEXT_HEIGHT_TAGLINE_CM = 1.12;

export const PRINT_CARD_DEFAULT_LOGO_MAX_WIDTH_CM = 1.8;
export const PRINT_CARD_DEFAULT_LOGO_MAX_HEIGHT_CM = 0.5;
export const PRINT_CARD_DEFAULT_NFC_SIZE_CM = 0.48;
export const PRINT_CARD_DEFAULT_QR_SIZE_CM = 1.16;
export const PRINT_CARD_DEFAULT_TAGLINE_TEXT =
  "Stop collecting emails.\nStart collecting customers";

export interface PrintCardPrinterOption {
  id: number;
  label: string;
}

export const DEFAULT_PRINT_CARD_PLASTIC_PAGE_SIZE_PRESET_ID = "plastic-85x55";

export function printCardMaterialForTemplateType(
  type: PrintCardTemplateType,
): PrintCardMaterial {
  return type === "PLASTIC_NFC" ? "plastic" : "paper";
}

export function defaultPrintCardPageSizePresetId(
  type: PrintCardTemplateType,
): string {
  return type === "PLASTIC_NFC"
    ? DEFAULT_PRINT_CARD_PLASTIC_PAGE_SIZE_PRESET_ID
    : PRINT_CARD_DEFAULT_PAGE_SIZE_PRESET_ID;
}

export function isPageSizePresetAllowedForTemplate(
  pageSizePresetId: string,
  allowedPageSizePresetIds?: readonly string[] | null,
): boolean {
  if (!allowedPageSizePresetIds?.length) {
    return true;
  }
  return allowedPageSizePresetIds.includes(pageSizePresetId);
}

export function printCardPageSizePresetsForTemplate(
  presets: readonly PrintCardPageSizePreset[],
  type: PrintCardTemplateType,
  allowedPageSizePresetIds?: readonly string[] | null,
): PrintCardPageSizePreset[] {
  const material = printCardMaterialForTemplateType(type);
  const forMaterial = presets.filter((preset) => preset.material === material);
  if (!allowedPageSizePresetIds?.length) {
    return [...forMaterial];
  }
  return forMaterial.filter((preset) =>
    allowedPageSizePresetIds.includes(preset.id),
  );
}

export function printCardDesignPageFromPreset(
  preset: PrintCardPageSizePreset,
  orientation: PrintCardOrientation = "landscape",
): PrintCardDesignPage {
  const portrait = orientation === "portrait";
  return {
    widthCm: portrait ? preset.heightCm : preset.widthCm,
    heightCm: portrait ? preset.widthCm : preset.heightCm,
    bleedMm: preset.bleedMm,
    safeZoneInsetMm: preset.safeZoneInsetMm,
  };
}

export const PRINT_CARD_TEMPLATE_TYPES: PrintCardTemplateType[] = [
  "PAPER",
  "PAPER_NFC",
  "PLASTIC_NFC",
];

export const PRINT_CARD_PAPER_QUANTITY_OPTIONS = [100, 250, 500, 1000] as const;
export const PRINT_CARD_MULTILOFT_QUANTITY_OPTIONS = [
  50, 100, 250, 500, 1000,
] as const;
export const PRINT_CARD_PLASTIC_QUANTITY_MIN = 1;
export const PRINT_CARD_PLASTIC_QUANTITY_MAX = 10_000;
export const PRINT_CARD_ALLOWED_QUANTITIES_MAX = 20;

const PRINT_CARD_GOOGLE_FONT_WEIGHTS = "100;200;300;400;500;600;700;800;900";

const PRINT_CARD_GOOGLE_FONT_CSS_URLS = {
  roboto: `https://fonts.googleapis.com/css2?family=Roboto:wght@${PRINT_CARD_GOOGLE_FONT_WEIGHTS}&display=block`,
  lato: `https://fonts.googleapis.com/css2?family=Lato:wght@${PRINT_CARD_GOOGLE_FONT_WEIGHTS}&display=block`,
  openSans: `https://fonts.googleapis.com/css2?family=Open+Sans:wght@${PRINT_CARD_GOOGLE_FONT_WEIGHTS}&display=block`,
  oswald: `https://fonts.googleapis.com/css2?family=Oswald:wght@${PRINT_CARD_GOOGLE_FONT_WEIGHTS}&display=block`,
  ptSans: `https://fonts.googleapis.com/css2?family=PT+Sans:wght@${PRINT_CARD_GOOGLE_FONT_WEIGHTS}&display=block`,
  dosis: `https://fonts.googleapis.com/css2?family=Dosis:wght@${PRINT_CARD_GOOGLE_FONT_WEIGHTS}&display=block`,
  quicksand: `https://fonts.googleapis.com/css2?family=Quicksand:wght@${PRINT_CARD_GOOGLE_FONT_WEIGHTS}&display=block`,
  lora: `https://fonts.googleapis.com/css2?family=Lora:wght@${PRINT_CARD_GOOGLE_FONT_WEIGHTS}&display=block`,
  raleway: `https://fonts.googleapis.com/css2?family=Raleway:wght@${PRINT_CARD_GOOGLE_FONT_WEIGHTS}&display=block`,
  montserrat: `https://fonts.googleapis.com/css2?family=Montserrat:wght@${PRINT_CARD_GOOGLE_FONT_WEIGHTS}&display=block`,
} as const;

export const PRINT_CARD_FONT_PRESETS: PrintCardFontPreset[] = [
  {
    id: "roboto",
    labelKey: "printCards.fontPresetRoboto",
    fontFamily: '"Roboto", Arial, sans-serif',
    fontCssUrl: PRINT_CARD_GOOGLE_FONT_CSS_URLS.roboto,
  },
  {
    id: "lato",
    labelKey: "printCards.fontPresetLato",
    fontFamily: '"Lato", Arial, sans-serif',
    fontCssUrl: PRINT_CARD_GOOGLE_FONT_CSS_URLS.lato,
  },
  {
    id: "open-sans",
    labelKey: "printCards.fontPresetOpenSans",
    fontFamily: '"Open Sans", Arial, sans-serif',
    fontCssUrl: PRINT_CARD_GOOGLE_FONT_CSS_URLS.openSans,
  },
  {
    id: "oswald",
    labelKey: "printCards.fontPresetOswald",
    fontFamily: '"Oswald", Arial, sans-serif',
    fontCssUrl: PRINT_CARD_GOOGLE_FONT_CSS_URLS.oswald,
  },
  {
    id: "pt-sans",
    labelKey: "printCards.fontPresetPtSans",
    fontFamily: '"PT Sans", Arial, sans-serif',
    fontCssUrl: PRINT_CARD_GOOGLE_FONT_CSS_URLS.ptSans,
  },
  {
    id: "dosis",
    labelKey: "printCards.fontPresetDosis",
    fontFamily: '"Dosis", Arial, sans-serif',
    fontCssUrl: PRINT_CARD_GOOGLE_FONT_CSS_URLS.dosis,
  },
  {
    id: "quicksand",
    labelKey: "printCards.fontPresetQuicksand",
    fontFamily: '"Quicksand", Arial, sans-serif',
    fontCssUrl: PRINT_CARD_GOOGLE_FONT_CSS_URLS.quicksand,
  },
  {
    id: "lora",
    labelKey: "printCards.fontPresetLora",
    fontFamily: '"Lora", Georgia, serif',
    fontCssUrl: PRINT_CARD_GOOGLE_FONT_CSS_URLS.lora,
  },
  {
    id: "raleway",
    labelKey: "printCards.fontPresetRaleway",
    fontFamily: '"Raleway", Arial, sans-serif',
    fontCssUrl: PRINT_CARD_GOOGLE_FONT_CSS_URLS.raleway,
  },
  {
    id: "montserrat",
    labelKey: "printCards.fontPresetMontserrat",
    fontFamily: '"Montserrat", Arial, sans-serif',
    fontCssUrl: PRINT_CARD_GOOGLE_FONT_CSS_URLS.montserrat,
  },
];

export interface PrintCardGeometryBox {
  xCm: number;
  yCm: number;
  widthCm: number;
  heightCm: number;
  rotationDeg?: number;
}

export interface PrintCardAabb {
  left: number;
  top: number;
  right: number;
  bottom: number;
  width: number;
  height: number;
  centerX: number;
  centerY: number;
}

export type SmartGuideAxis = "x" | "y";

export interface PrintCardGuideGeometry {
  id: string;
  xCm: number;
  yCm: number;
  widthCm: number;
  heightCm: number;
  rotationDeg?: number;
}

export interface PrintCardGuidePage {
  widthCm: number;
  heightCm: number;
  originXCm?: number;
  originYCm?: number;
}

export interface SmartGuideLine {
  axis: SmartGuideAxis;
  positionCm: number;
  startCm: number;
  endCm: number;
  kind: "edge" | "center";
}

export interface SmartGuideSpacing {
  axis: SmartGuideAxis;
  xCm: number;
  yCm: number;
  widthCm: number;
  heightCm: number;
  sizeCm: number;
  equal: boolean;
}

export interface SmartGuidesView {
  lines: SmartGuideLine[];
  spacings: SmartGuideSpacing[];
}

export interface SnapMoveInput {
  moving: PrintCardGuideGeometry;
  siblings: PrintCardGuideGeometry[];
  page: PrintCardGuidePage;
  thresholdCm: number;
  enabled: boolean;
  measure: boolean;
}

export interface SnapMoveResult {
  xCm: number;
  yCm: number;
  view: SmartGuidesView;
}

export interface SnapResizeInput extends SnapMoveInput {
  handle: string;
  keepSquare: boolean;
}

export interface SnapResizeResult {
  xCm: number;
  yCm: number;
  widthCm: number;
  heightCm: number;
  view: SmartGuidesView;
}
